import { useTheme } from '@mui/material/styles';
import { Cell, Legend, Pie, PieChart, Tooltip } from 'recharts';

import { AnimatedBumpSector } from './AnimatedBumpSector';
import { ReportWidgetTooltipContent } from './ReportWidgetTooltipContent';

import type {
    ReportWidgetChartPoint,
    ReportWidgetItem,
} from '../../model/types';
import type { Sector, TooltipProps } from 'recharts';

const DEFAULT_MIN_ANGLE = 4;
const SLICE_GAP_STROKE_WIDTH = 2.5;

interface ReportWidgetPieChartProps {
    data: ReportWidgetChartPoint[];
    showLegend: boolean;
    tooltipFormatter: (value: number) => string;
    items: ReportWidgetItem[];
    total: number;
    onItemClick?: (item: ReportWidgetItem) => void;
    variant: 'pie' | 'donut';
    width?: number;
    height?: number;
    activeItemId?: string;
    disableAnimation?: boolean;
    minAngle?: number;
}

export const ReportWidgetPieChart = ({
    data,
    showLegend,
    tooltipFormatter,
    items,
    total,
    onItemClick,
    variant,
    width,
    height,
    activeItemId,
    disableAnimation = false,
    minAngle = DEFAULT_MIN_ANGLE,
}: ReportWidgetPieChartProps) => {
    const handlePieClick = (entry: { id?: string }) => {
        if (!onItemClick || !entry?.id) return;
        const item = items.find((i) => i.id === entry.id);
        if (!item) return;

        if (item.meta?.isAggregatedOther) return;

        onItemClick(item);
    };

    const theme = useTheme();
    const gapColor = theme.palette.background.paper;

    const activeIndex = activeItemId
        ? data.findIndex((entry) => entry.id === activeItemId)
        : -1;

    return (
        <PieChart width={width} height={height}>
            <Pie
                data={data}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius={variant === 'donut' ? '40%' : 0}
                outerRadius="80%"
                paddingAngle={0}
                minAngle={minAngle}
                activeIndex={activeIndex >= 0 ? activeIndex : undefined}
                isAnimationActive={!disableAnimation}
                animationDuration={250}
                animationEasing="ease-out"
                activeShape={(props: React.ComponentProps<typeof Sector>) => (
                    <AnimatedBumpSector
                        {...props}
                        variant={variant}
                        disableAnimation={disableAnimation}
                    />
                )}
                onClick={handlePieClick}
                cursor={onItemClick ? 'pointer' : 'default'}
            >
                {data.map((entry, index) => {
                    const isActive = index === activeIndex;
                    return (
                        <Cell
                            key={entry.id}
                            fill={entry.color}
                            stroke={gapColor}
                            strokeWidth={SLICE_GAP_STROKE_WIDTH}
                            fillOpacity={
                                activeIndex >= 0 ? (isActive ? 1 : 0.4) : 1
                            }
                        />
                    );
                })}
            </Pie>
            <Tooltip
                content={(props: TooltipProps<number, string>) => (
                    <ReportWidgetTooltipContent
                        active={props.active}
                        payload={props.payload}
                        label={props.label}
                        tooltipFormatter={tooltipFormatter}
                        total={total}
                        items={items}
                    />
                )}
            />
            {showLegend && <Legend />}
        </PieChart>
    );
};
